import React, { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import bannerCamp from '../../assets/banner_camp.png';
import FilterSidebar from './elements/FilterSidebar';
import MapMode from './elements/MapMode';
import ListMode from './elements/ListMode';

const ExplorePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get view mode from URL query params, default to 'map'
  const viewMode = (searchParams.get('mode') as 'map' | 'list') || 'map';

  useEffect(() => {
    // If no mode is set in URL, set it to 'map' by default
    if (!searchParams.get('mode')) {
      setSearchParams({ mode: 'map' });
    }
  }, [searchParams, setSearchParams]);

  const getModeLabel = () => {
    return viewMode === 'map' ? 'Map Mode' : 'List Mode';
  };

  const setViewMode = (mode: 'map' | 'list') => {
    setSearchParams({ mode });
  };

  return (
    <div className='bg-white min-h-screen'>
      {/* Hero Banner Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pt-24 pb-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <Link
            to="/explore"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            Explore
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span
            className="text-gray-700"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            {getModeLabel()}
          </span>
        </div>

        {/* Banner */}
        <div className="relative w-full h-[400px] rounded-[32px] overflow-hidden">
          <img
            src={bannerCamp}
            alt="Camp Banner"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Controls Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-12">
        <div className="flex items-center justify-between">
          {/* Left: Mode Toggle and Count */}
          <div className="flex items-center gap-6">
            {/* Mode Toggle Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('map')}
                className={`transition-all ${
                  viewMode === 'map'
                    ? 'bg-black text-white border border-black'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  fontWeight: 500,
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  borderRadius: '20px',
                  height: '44px'
                }}
              >
                Map Mode
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`transition-all ${
                  viewMode === 'list'
                    ? 'bg-black text-white border border-black'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  fontWeight: 500,
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  borderRadius: '20px',
                  height: '44px'
                }}
              >
                List Mode
              </button>
            </div>

            {/* Campsite Count */}
            <h2
              className="text-black"
              style={{
                fontFamily: 'Poppins',
                fontSize: '32px',
                fontWeight: 700
              }}
            >
              12 Campsites
            </h2>
          </div>

          {/* Right: Sort Dropdown */}
          <div className="relative">
            <select
              className="appearance-none bg-white border border-gray-300 text-gray-700 hover:border-gray-400 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
              style={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 400,
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '28px',
                paddingRight: '48px',
                borderRadius: '20px',
                height: '44px'
              }}
            >
              <option>Sort by: Popular</option>
              <option>Sort by: Rating</option>
              <option>Sort by: Distance</option>
              <option>Sort by: Name</option>
            </select>
            <ChevronRight className="absolute right-4 top-1/2 transform -translate-y-1/2 rotate-90 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Content Section - Filter + Map/List */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-12">
        <div className="flex gap-6">
          {/* Filter Sidebar */}
          <FilterSidebar />

          {/* Content Area */}
          {viewMode === 'map' ? <MapMode /> : <ListMode />}
        </div>
      </div>
    </div>
  );
};

export default ExplorePage;
